// src/Login.js
//
// WHAT THIS FILE IS:
// This is the login page component. A "component" in React is just a
// JavaScript function that returns JSX (HTML-like syntax) describing
// what should appear on screen. This one renders a login form and talks
// to our Django API to get an authentication token.

import { useState } from 'react';
import { API_BASE_URL } from './apiConfig';
// useState is a React "Hook" - a special function that lets a component
// remember values BETWEEN re-renders (normal JavaScript variables reset
// every time a function re-runs, but useState values persist).

// Login receives one PROP from its parent (App.js): onLogin, a function
// to call once we successfully get a token back from the server. This
// is how a CHILD component (Login) sends information UP to its PARENT
// (App) - by calling a function the parent handed down to it.
function Login({ onLogin }) {
  // Each useState call gives us: [currentValue, functionToUpdateIt].
  // We start username and password as empty strings, and error as an
  // empty string too (meaning "no error currently").
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // handleSubmit runs when the form is submitted (button clicked or
  // Enter pressed inside an input). It's an "async" function because it
  // needs to wait for a network request (fetch) to finish before
  // continuing.
  const handleSubmit = async (e) => {
    e.preventDefault();
    // preventDefault() stops the browser's DEFAULT behavior for a form
    // submit, which is to reload the entire page. We want React to
    // handle this instead, without a full page reload.

    try {
      // fetch() sends an HTTP request. We're POSTing to Django's login
      // endpoint with the username/password as JSON in the request body.
      const response = await fetch(`${API_BASE_URL}/api/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // JSON.stringify converts our JavaScript object into a JSON
        // text string, since that's the format HTTP request bodies need.
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        // response.ok is false for any error status (401, 403, 500...).
        // We don't try to read the token in that case - we show an error.
        setError('Invalid username or password');
        return;
      }

      // .json() parses the response body text back into a JavaScript
      // object. Django's obtain_auth_token view returns { "token": "..." }.
      const data = await response.json();

      // Call the function our PARENT gave us, passing the token up so
      // App.js can store it and switch from showing Login to showing
      // the dashboard.
      onLogin(data.token);
    } catch (err) {
      // This catch block runs if the network request itself fails
      // entirely (e.g. Django server isn't running) - a different
      // failure case than "wrong password".
      setError('Could not connect to the server');
    }
  };

  return (
    // min-h-screen = at least full viewport height
    // flex items-center justify-center = centers the login card both
    // vertically and horizontally on the page
    // bg-gray-100 = light gray page background
    <div className="min-h-screen flex items-center justify-center bg-gray-100">

      {/* The white card itself: rounded corners, border, shadow, fixed width */}
      <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200 w-80">

        <h1 className="text-xl font-bold mb-6 text-center">PMIS Login</h1>

        {/* onSubmit on the <form> itself, not the button - this way
            pressing Enter inside an input also triggers handleSubmit,
            not just clicking the button. */}
        <form onSubmit={handleSubmit}>

          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
          <input
            type="text"
            // value={username} makes this a "controlled input" - its
            // displayed value is always driven by React state, not by
            // the browser's own internal input memory.
            value={username}
            // onChange fires on every keystroke. e.target.value is the
            // input's current text, which we save into state.
            onChange={(e) => setUsername(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
            // focus:ring-2 focus:ring-blue-400 = adds a visible blue
            // glow ring around the input only WHEN it's focused/clicked
          />

          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          {/* Only render the error message paragraph if error is not an
              empty string. In JavaScript, "" is falsy, so
              error && <p>...</p> shows nothing when error is empty, and
              shows the <p> when error has text. */}
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
          >
            Log In
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
// export default makes this function importable in other files via
// import Login from './Login';
